import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert, Box, Button, Checkbox, FormControl, IconButton, InputLabel, MenuItem, Select, Stack,
    Table, TableBody, TableCell, TableFooter, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import EnergyApiService from '../../api/EnergyApiService';
import SaveButton from '../Common/SaveButton';
import { useSnackbar } from '../Common/SnackbarProvider';
import { useAsyncCallback } from '../../hooks/useAsyncCallback';
import {
    ANOMALY_TOLERANCE, ISO_DATE, formatQuantity, isRowDirty, medianRate, newRowKey, parseQuantity,
    periodDays, periodIssues, rateDeviation,
} from './energyEntryUtils';

const energyApiService = new EnergyApiService();

/** Days a new period covers when there is nothing to follow on from. */
const DEFAULT_PERIOD_DAYS = 30;

function toRow (bill) {
    const saved = {
        periodStart: bill.periodStart,
        periodEnd: bill.periodEnd,
        consumption: bill.consumption ?? '',
        unitId: bill.unitId,
        isEstimated: bill.isEstimated,
        statementDate: bill.statementDate ?? '',
    };

    return {
        key: `id-${bill.id}`,
        id: bill.id,
        meterId: bill.meterId,
        unitCode: bill.unitCode,
        consumptionKBtu: bill.consumptionKBtu,
        source: bill.source,
        ...saved,
        saved,
    };
}

/**
 * Utility readings for one meter, one row per billing period.
 *
 * Periods are the real read-to-read dates, never snapped to a calendar month: degree days are
 * summed over the actual span, so a period forced onto month boundaries would bias the baseline.
 */
export default function UtilityBillEntryGrid ({ siteId, year, canEdit: canEditSite, meters, units }) {
    const { t } = useTranslation();
    const snackbar = useSnackbar();

    const [meterId, setMeterId] = useState(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    const from = `${year}-01-01`;
    const to = `${year}-12-31`;

    const meter = useMemo(() => meters.find(m => m.id === meterId) ?? null, [meters, meterId]);

    // Interval meters are fed by their data source; the server refuses hand entry on them, so the
    // grid shows the readings without pretending they can be typed over.
    const canEdit = canEditSite && meter?.resolution === 'BilledOnly';

    useEffect(() => {
        if (meters.length === 0) {
            setMeterId(null);
            return;
        }

        if (!meters.some(m => m.id === meterId)) {
            setMeterId(meters[0].id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [meters]);

    const load = useCallback(async () => {
        if (!siteId) return;

        setLoading(true);
        try {
            const response = await energyApiService.getBills(siteId, from, to);
            if (!response?.success) {
                snackbar.error(response?.message ?? t('siteData.toast.loadFailed'));
                return;
            }

            setRows(response.data.map(toRow));
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteId, from, to]);

    useEffect(() => { load(); }, [load]);

    const meterRows = useMemo(
        () => rows.filter(row => row.meterId === meterId).sort((a, b) => a.periodStart.localeCompare(b.periodStart)),
        [rows, meterId]);

    const unitOptions = useMemo(() => {
        if (!meter) return units;

        // Other covers units that do not belong to one commodity; the server applies the same rule.
        return units.filter(unit => unit.commodity === meter.commodity || unit.commodity === 'Other');
    }, [units, meter]);

    const updateRow = (key, field, value) => {
        setRows(current => current.map(row => (row.key === key ? { ...row, [field]: value } : row)));
    };

    const addPeriod = () => {
        if (!meter) return;

        const last = meterRows[meterRows.length - 1];
        const start = last?.periodEnd ? dayjs(last.periodEnd).add(1, 'day') : dayjs(`${year}-01-01`);

        setRows(current => [
            ...current,
            {
                key: newRowKey(),
                id: null,
                meterId,
                periodStart: start.format(ISO_DATE),
                periodEnd: start.add(DEFAULT_PERIOD_DAYS - 1, 'day').format(ISO_DATE),
                consumption: '',
                unitId: meter.defaultUnitId,
                unitCode: meter.defaultUnitCode,
                isEstimated: false,
                statementDate: '',
                consumptionKBtu: null,
                source: 'Manual',
                saved: null,
            },
        ]);
    };

    const removeRow = async (row) => {
        if (row.id === null) {
            setRows(current => current.filter(r => r.key !== row.key));
            return;
        }

        const response = await energyApiService.deleteBill(row.id);
        if (!response?.success) {
            snackbar.error(response?.message ?? t('siteData.toast.deleteFailed'));
            return;
        }

        snackbar.success(t('siteData.utilities.toast.deleted'));
        setRows(current => current.filter(r => r.key !== row.key));
    };

    const dirtyRows = useMemo(() => meterRows.filter(isRowDirty), [meterRows]);
    const issues = useMemo(() => periodIssues(meterRows), [meterRows]);
    const median = useMemo(() => medianRate(meterRows), [meterRows]);

    const blocked = useMemo(
        () => Object.values(issues).some(issue => issue.type === 'overlap' || issue.type === 'outOfOrder'),
        [issues]);

    const total = useMemo(() => meterRows.reduce((sum, row) => {
        const value = parseQuantity(row.consumption);
        return sum + (value && !Number.isNaN(value) ? value : 0);
    }, 0), [meterRows]);

    const { run: save, loading: saving } = useAsyncCallback(async () => {
        const invalid = dirtyRows.find(row =>
            !row.periodStart || !row.periodEnd || Number.isNaN(parseQuantity(row.consumption)));

        if (invalid) {
            snackbar.error(t('siteData.toast.checkRows'));
            return;
        }

        const response = await energyApiService.saveBills(meterId, dirtyRows.map(row => ({
            id: row.id,
            periodStart: row.periodStart,
            periodEnd: row.periodEnd,
            consumption: parseQuantity(row.consumption),
            unitId: row.unitId,
            isEstimated: row.isEstimated,
            statementDate: row.statementDate || null,
        })));

        if (!response?.success) {
            snackbar.error(response?.message ?? t('siteData.toast.saveFailed'));
            return;
        }

        snackbar.success(t('siteData.utilities.toast.saved', { count: dirtyRows.length }));
        await load();
    });

    const issueFor = (row) => {
        const issue = issues[row.key];
        if (!issue) return null;

        if (issue.type === 'overlap') return t('siteData.issue.overlap');
        if (issue.type === 'outOfOrder') return t('siteData.issue.outOfOrder');
        return t('siteData.issue.gap', { count: issue.days });
    };

    if (meters.length === 0) {
        return <Alert severity="info">{t('siteData.utilities.noMeters')}</Alert>;
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack spacing={2}>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                    <FormControl size="small" sx={{ minWidth: 320 }}>
                        <InputLabel id="meter-label">{t('siteData.utilities.meter')}</InputLabel>
                        <Select
                            labelId="meter-label"
                            label={t('siteData.utilities.meter')}
                            value={meterId ?? ''}
                            onChange={event => setMeterId(event.target.value)}
                        >
                            {meters.map(option => (
                                <MenuItem key={option.id} value={option.id}>
                                    {t(`siteData.commodity.${option.commodity}`)} · {option.utilityName} · {option.accountNumber}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Typography variant="body2" color="text.secondary">
                        {t('siteData.utilities.yearTotal', { total: formatQuantity(total, 0), unit: meter?.defaultUnitCode ?? '' })}
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    {canEdit && (
                        <Button size="small" startIcon={<AddIcon />} onClick={addPeriod}>
                            {t('siteData.utilities.addPeriod')}
                        </Button>
                    )}
                </Stack>

                {meter && meter.resolution !== 'BilledOnly' && (
                    <Alert severity="info">{t('siteData.utilities.intervalMeter')}</Alert>
                )}
                {meter && meter.resolution === 'BilledOnly' && meter.dataSource !== 'Manual' && (
                    <Alert severity="info">{t('siteData.utilities.fedAutomatically', { source: meter.dataSource })}</Alert>
                )}
                {blocked && <Alert severity="warning">{t('siteData.issue.blockedSave')}</Alert>}

                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 180 }}>{t('siteData.column.periodStart')}</TableCell>
                            <TableCell sx={{ width: 180 }}>{t('siteData.column.periodEnd')}</TableCell>
                            <TableCell align="right" sx={{ width: 70 }}>{t('siteData.column.days')}</TableCell>
                            <TableCell align="right" sx={{ width: 160 }}>{t('siteData.column.consumption')}</TableCell>
                            <TableCell sx={{ width: 120 }}>{t('siteData.column.unit')}</TableCell>
                            <TableCell align="right" sx={{ width: 120 }}>{t('siteData.column.kbtu')}</TableCell>
                            <TableCell align="center" sx={{ width: 90 }}>
                                <Tooltip title={t('siteData.column.estimatedHelp')}>
                                    <span>{t('siteData.column.estimated')}</span>
                                </Tooltip>
                            </TableCell>
                            <TableCell sx={{ width: 180 }}>{t('siteData.column.statementDate')}</TableCell>
                            <TableCell sx={{ width: 56 }} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {meterRows.map(row => {
                            const days = periodDays(row.periodStart, row.periodEnd);
                            const consumption = parseQuantity(row.consumption);
                            const deviation = rateDeviation(row, median, 'consumption');
                            const flagged = deviation !== null && Math.abs(deviation) > ANOMALY_TOLERANCE;
                            const issue = issueFor(row);

                            return (
                                <TableRow key={row.key} sx={{ backgroundColor: isRowDirty(row) ? 'rgba(153, 194, 14, 0.08)' : undefined }}>
                                    <TableCell>
                                        <DesktopDatePicker
                                            inputFormat="YYYY-MM-DD"
                                            value={row.periodStart ? dayjs(row.periodStart) : null}
                                            disabled={!canEdit}
                                            onChange={value => updateRow(row.key, 'periodStart', value ? value.format(ISO_DATE) : '')}
                                            renderInput={params => <TextField {...params} size="small" variant="standard" />}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Stack spacing={0.5}>
                                            <DesktopDatePicker
                                                inputFormat="YYYY-MM-DD"
                                                value={row.periodEnd ? dayjs(row.periodEnd) : null}
                                                disabled={!canEdit}
                                                onChange={value => updateRow(row.key, 'periodEnd', value ? value.format(ISO_DATE) : '')}
                                                renderInput={params => <TextField {...params} size="small" variant="standard" />}
                                            />
                                            {issue && <Typography variant="caption" color="warning.dark">{issue}</Typography>}
                                        </Stack>
                                    </TableCell>
                                    <TableCell align="right">{days ?? '—'}</TableCell>
                                    <TableCell align="right">
                                        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                                            {flagged && (
                                                <Tooltip title={t('siteData.issue.offMedian', { percent: Math.round(deviation * 100) })}>
                                                    <WarningAmberIcon fontSize="small" color="warning" />
                                                </Tooltip>
                                            )}
                                            <TextField
                                                size="small"
                                                variant="standard"
                                                inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                                                value={row.consumption}
                                                disabled={!canEdit}
                                                error={Number.isNaN(consumption)}
                                                onChange={event => updateRow(row.key, 'consumption', event.target.value)}
                                            />
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Select
                                            variant="standard"
                                            size="small"
                                            fullWidth
                                            value={row.unitId ?? ''}
                                            disabled={!canEdit}
                                            onChange={event => updateRow(row.key, 'unitId', event.target.value)}
                                        >
                                            {unitOptions.map(unit => (
                                                <MenuItem key={unit.id} value={unit.id}>{unit.code}</MenuItem>
                                            ))}
                                        </Select>
                                    </TableCell>
                                    <TableCell align="right">
                                        {row.consumptionKBtu === null || row.consumptionKBtu === undefined
                                            ? '—'
                                            : formatQuantity(row.consumptionKBtu, 0)}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Checkbox
                                            size="small"
                                            checked={!!row.isEstimated}
                                            disabled={!canEdit}
                                            inputProps={{ 'aria-label': t('siteData.column.estimated') }}
                                            onChange={event => updateRow(row.key, 'isEstimated', event.target.checked)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <DesktopDatePicker
                                            inputFormat="YYYY-MM-DD"
                                            value={row.statementDate ? dayjs(row.statementDate) : null}
                                            disabled={!canEdit}
                                            onChange={value => updateRow(row.key, 'statementDate', value ? value.format(ISO_DATE) : '')}
                                            renderInput={params => <TextField {...params} size="small" variant="standard" />}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {canEdit && (
                                            <IconButton
                                                size="small"
                                                aria-label={t('siteData.action.deleteRow')}
                                                onClick={() => removeRow(row)}
                                            >
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {meterRows.length === 0 && !loading && (
                            <TableRow>
                                <TableCell colSpan={9}>
                                    <Typography variant="body2" color="text.secondary">{t('siteData.utilities.empty')}</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TableCell colSpan={3}>{t('siteData.column.total')}</TableCell>
                            <TableCell align="right">{formatQuantity(total, 1)}</TableCell>
                            <TableCell colSpan={5} />
                        </TableRow>
                    </TableFooter>
                </Table>

                {canEdit && (
                    <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                            {dirtyRows.length > 0 ? t('siteData.unsavedRows', { count: dirtyRows.length }) : ''}
                        </Typography>
                        <Button disabled={dirtyRows.length === 0 || saving} onClick={load}>{t('siteData.action.discard')}</Button>
                        <SaveButton
                            variant="contained"
                            loading={saving}
                            disabled={dirtyRows.length === 0 || blocked}
                            onClick={save}
                        >
                            {t('siteData.action.save')}
                        </SaveButton>
                    </Stack>
                )}
            </Stack>
        </LocalizationProvider>
    );
}

UtilityBillEntryGrid.propTypes = {
    siteId: PropTypes.number,
    year: PropTypes.number.isRequired,
    /** Whether the user may write to this location at all; interval meters stay read-only regardless. */
    canEdit: PropTypes.bool,
    meters: PropTypes.array.isRequired,
    units: PropTypes.array.isRequired,
};
